import { useFlags } from "launchdarkly-react-client-sdk";
import { type ReactNode, useCallback, useRef, useState } from "react";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { misc } from "app/themes/colors";
import { AMBIENT_SCRIBE_SERVER_AUDIO_DOWNLOAD } from "constants/feature-flags";

import { useScribeAudioDownload } from "./hooks/use-scribe-audio-download.hook";
import ScribeAudioDownloadButton from "./scribe-audio-download-button.component";

type ScribeAudioPlayerProps = {
  /** Streamable audio URL. When present the native player and play button are shown. */
  audioUrl?: string;
  remoteMedicalScribeId: number | undefined;
  localId: string | undefined;
  remoteRecordingUrl: string | undefined;
  /** FE status. With AMBIENT_SCRIBE_SERVER_AUDIO_DOWNLOAD on, it explains a disabled download. */
  status?: number;
  disabled?: boolean;
  trailingActions?: ReactNode;
};

export default function ScribeAudioPlayer({
  audioUrl,
  remoteMedicalScribeId,
  localId,
  remoteRecordingUrl,
  status,
  disabled,
  trailingActions
}: Readonly<ScribeAudioPlayerProps>) {
  const theme = useTheme();
  const isServerAudioDownloadEnabled =
    useFlags()[AMBIENT_SCRIBE_SERVER_AUDIO_DOWNLOAD] === true;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);

  const { download, isDownloading } = useScribeAudioDownload({
    remoteMedicalScribeId,
    localId,
    remoteRecordingUrl
  });

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch((error) => {
        console.error("Audio playback failed:", error);
        setPlaybackError(true);
      });
    } else {
      audio.pause();
    }
  }, []);

  const buttonSx = {
    boxShadow: theme.shadows[2],
    alignSelf: "flex-start",
    background: misc.veryLightGreen
  };

  return (
    <Stack rowGap={2} sx={{ mt: 0.5, width: "100%" }}>
      {audioUrl && (
        <Box>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            key={audioUrl}
            ref={audioRef}
            controls
            src={audioUrl}
            style={{ width: "100%" }}
            onPlay={() => { setIsPlaying(true); setPlaybackError(false); }}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onCanPlay={() => setPlaybackError(false)}
          />
          {playbackError && (
            <Typography variant="caption" color="error">
              Audio could not be played. The recording may be unavailable or use an unsupported format.
            </Typography>
          )}
        </Box>
      )}

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {audioUrl && (
          <Button
            startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            variant="outlined"
            disabled={disabled}
            onClick={handlePlayPause}
            size="small"
            sx={{ ...buttonSx, flex: 1, minWidth: 140 }}
          >
            {isPlaying ? "Pause" : "Play Audio"}
          </Button>
        )}

        {Boolean(localId || remoteMedicalScribeId) && isServerAudioDownloadEnabled && (
          <ScribeAudioDownloadButton
            remoteMedicalScribeId={remoteMedicalScribeId}
            localId={localId}
            remoteRecordingUrl={remoteRecordingUrl}
            status={status}
            disabled={disabled}
          />
        )}

        {Boolean(localId || remoteMedicalScribeId) && !isServerAudioDownloadEnabled && (
          <LoadingButton
            loading={isDownloading}
            startIcon={<FileDownloadIcon />}
            variant="reportDownload"
            disabled={disabled || isDownloading}
            onClick={download}
            size="small"
            sx={{ boxShadow: theme.shadows[2], alignSelf: "flex-start" }}
          >
            Download Local Audio
          </LoadingButton>
        )}

        {trailingActions}
      </Stack>
    </Stack>
  );
}
